import { Box, Button, Skeleton } from "@mui/material";
import EmptyState from "global/components/EmptyState/EmptyState";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import { maskedContent } from "helpers/maskedContent";
import { ContractDocument } from "screens/ContractSearch/ContractSearchTypes";
import { filterContracts, libraryRowMeta } from "../ContractCompareHelper";
import { safeFileName } from "../ContractCompareTypesAndValidation";
import { LibraryStatus } from "../useContractLibrary";
import styles from "../ContractCompare.styles";
import t from "../ContractCompare.tokens";
import { AlertCircleGlyph } from "./ContractCompareIcons";
import KindTile from "./KindTile";

interface LibraryListProps {
  status: LibraryStatus;
  /** Every PDF / Word contract of the user. */
  contracts: ContractDocument[];
  query: string;
  selectedId: string | null;
  /** The pick is shown at once even while the list still loads (router state from Contract Search). */
  pending?: { documentId: string; fileName: string } | null;
  onSelect: (document: ContractDocument) => void;
  onRetry: () => void;
  onClearQuery: () => void;
  /** One radio group per list, so the phone sheet and the page never share a name. */
  name: string;
  inSheet?: boolean;
  /** The plan or role blocks the page: the radios are not offered. */
  disabled?: boolean;
}

const SKELETON_ROWS = 5;

/** A skeleton row built from the real row's own style objects, so it occupies the same box (CLAUDE.md §11.9). */
const SkeletonRow = ({ last }: { last: boolean }) => (
  <Box
    component="li"
    sx={styles.row(false, last)}
    aria-hidden="true"
  >
    <Skeleton variant="circular" width={t.list.radio} height={t.list.radio} animation="wave" />
    <Skeleton variant="rounded" width={t.kind.width} height={t.kind.height} animation="wave" />
    <Box sx={styles.rowText}>
      {/* The text lines take the real name and meta line's own font size and line height, so the box matches. */}
      <Skeleton variant="text" width="62%" animation="wave" sx={styles.fileName} />
      <Skeleton variant="text" width="38%" animation="wave" sx={styles.fileMeta} />
    </Box>
  </Box>
);

/**
 * The old-version list: one choice, like radio buttons. Each row is a real `<label>` around a native radio input,
 * so the group, the arrow keys and the selected state come from the platform. The names are customer data: masked
 * from session recording (D41).
 */
const LibraryList = ({
  status,
  contracts,
  query,
  selectedId,
  pending,
  onSelect,
  onRetry,
  onClearQuery,
  name,
  inSheet = false,
  disabled = false,
}: LibraryListProps) => {
  const page = strings.contractCompare.page;

  // The list was never asked for (the page is blocked): nothing to show, and above all no "no contracts" message.
  if (status === "skipped") return null;

  if (status === "loading") {
    return (
      <Box
        component="ul"
        sx={inSheet ? styles.listInSheet : styles.list}
        aria-busy="true"
        aria-label={page.libraryListLabel}
      >
        {pending && (
          <Box component="li" sx={styles.row(true)} {...maskedContent}>
            <Box sx={styles.radio(true)} aria-hidden="true" />
            <KindTile fileName={pending.fileName} />
            <Box sx={styles.rowText}>
              <Box component="span" dir="auto" sx={styles.fileName}>
                {safeFileName(pending.fileName)}
              </Box>
            </Box>
          </Box>
        )}
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <SkeletonRow key={index} last={index === SKELETON_ROWS - 1} />
        ))}
      </Box>
    );
  }

  if (status === "failed") {
    return (
      <Box sx={styles.emptyList} role="alert">
        <AlertCircleGlyph sx={{ width: 28, height: 28 }} />
        <Box component="p" sx={{ m: 0 }}>
          {page.libraryFailed}
        </Box>
        <Button variant="outlined" onClick={onRetry} sx={styles.actionButton}>
          {page.retry}
        </Button>
      </Box>
    );
  }

  if (!contracts.length) {
    return (
      <EmptyState size="medium" icon="doc" accent="violet" title={page.libraryEmpty} />
    );
  }

  const shown = filterContracts(contracts, query);
  if (!shown.length) {
    // The typed query is usually a piece of a file name: masked from session recording (D41).
    return (
      <Box {...maskedContent}>
        <EmptyState
          size="medium"
          icon="search"
          accent="violet"
          title={fillCopy(page.libraryNoMatchTitle, { query: query.trim() })}
          body={page.libraryNoMatchText}
          primaryAction={{ label: page.clearSearch, onClick: onClearQuery }}
        />
      </Box>
    );
  }

  return (
    <Box
      role="radiogroup"
      aria-label={page.libraryListLabel}
      sx={inSheet ? styles.listInSheet : styles.list}
    >
      {shown.map((document, index) => {
        const selected = document.documentId === selectedId;
        const meta = libraryRowMeta(document);
        // From the row's place, never the document id: analytics tools record the `id` of a clicked input (D22, D59).
        const inputId = `${name}-${index}`;
        return (
          <Box key={document.documentId}>
            <Box
              component="label"
              htmlFor={inputId}
              sx={styles.row(selected)}
              {...maskedContent}
            >
              <Box
                component="input"
                id={inputId}
                type="radio"
                name={name}
                checked={selected}
                disabled={disabled}
                onChange={() => onSelect(document)}
                aria-describedby={meta ? `${inputId}-meta` : undefined}
                sx={styles.srOnly}
              />
              <Box component="span" className="compare-row-ring" sx={styles.rowRing} aria-hidden="true" />
              <Box sx={styles.radio(selected)} aria-hidden="true" />
              <KindTile fileName={document.filename} />
              <Box sx={styles.rowText}>
                <Box component="span" dir="auto" sx={styles.fileName}>
                  {safeFileName(document.filename)}
                </Box>
                {meta && (
                  <Box component="span" id={`${inputId}-meta`} sx={styles.fileMeta}>
                    {meta}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default LibraryList;
